import { Button, Checkbox, TextInput, Hyperlink, Spacing, FormField } from 'zens-storybook'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet } from 'react-native'
import { object, string } from 'yup'
import { ApiError, useStore } from 'zens-store'
import { useRouter, useNumberParams, useParams, Link, Redirect } from 'zens-router'
import { AuthPanel } from '@dms/component'
import { ValidationError } from '@dms/helper'

const styles = StyleSheet.create({
  input: {
    marginBottom: Spacing.XS,
    minHeight: 92,
  },
  email: {
    marginHorizontal: Spacing.XL,
    marginBottom: Spacing.L,
  },
  button: {
    minWidth: 100,
  },
})

const AuthSignIn = () => {
  const { t } = useTranslation()
  const [{ companyId }] = useNumberParams()
  const [{ email }] = useParams()
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const router = useRouter()
  const { auth, message } = useStore()

  const schema = object({
    email: string().required(t('Email address is required')),
    password: string().required(t('A password is required')),
  })

  useEffect(() => {
    setErrors({})
  }, [password])

  const signIn = async () => {
    try {
      schema.validateSync({ email, password, remember })
    } catch (error) {
      const { message, path } = error as ValidationError
      setErrors({ [path]: message })
      return
    }
    setLoading(true)
    try {
      setErrors({})
      await auth.signIn({ email: email!, password })
      if (remember) await auth.rememberAuth()
      if (companyId) router.push(`/companies/${companyId}`)
      else router.push(`/`)
      setLoading(false)
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`Auth: Sign in failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Sign in failed')}. ${errorMessage}`,
          status: 'error',
        })
      }
      setLoading(false)
    }
  }

  const [{ email: emailParam }] = useParams()

  if (email === undefined) {
    return <Redirect href={{ pathname: '/forbidden' }} />
  }

  return (
    <AuthPanel
      backHref={{ pathname: `/email`, params: { companyId, email } }}
      title={t('Welcome')}
      bottom={
        <Button
          title={t('Sign in')}
          style={styles.button}
          onPress={signIn}
          testID="AuthSignIn.Submit"
          disabled={loading}
        />
      }
    >
      <FormField disabled error={errors.email} style={styles.email}>
        <TextInput value={email} disabled error={!!errors.email} round />
      </FormField>
      <FormField
        label={t('Password')}
        error={errors.password}
        header={
          <Link href={{ pathname: `/forgot`, params: { email, companyId } }} asChild>
            <Hyperlink title={t('Forgot password?')} testID="AuthSignIn.Forgot" />
          </Link>
        }
        style={styles.input}
      >
        <TextInput
          placeholder={t('Enter your password here')}
          value={password}
          onChangeText={setPassword}
          onSubmitEditing={signIn}
          autoCapitalize="none"
          autoComplete="password"
          error={!!errors.password}
          secureTextEntry
          autoFocus
          testID="AuthSignIn.Password"
        />
      </FormField>
      <Checkbox
        label={t('Keep me signed in')}
        value={remember}
        onValueChange={setRemember}
        testID="AuthSignIn.Remember"
      />
    </AuthPanel>
  )
}

export default AuthSignIn
